import {
  faArrowLeft,
  faArrowRight,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router';

import routesConstants from '@/routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { BatchSession } from '../../batchSession';
import { batchSessionAtom } from '../../batchSession';
import { buildClaimPath } from '../../helpers';

interface BatchHeaderProps {
  session: BatchSession;
  /** Injected between the attachment label and the centered buttons (e.g. push error message) */
  leftContent?: React.ReactNode;
  /** The center action buttons (Push to PMS, Download, Print) */
  rightContent?: React.ReactNode;
}

export const BatchHeader = ({
  session,
  leftContent,
  rightContent,
}: BatchHeaderProps) => {
  const { t } = useLingui();
  const history = useHistory();
  const { unifiedId } = useParams<{ unifiedId: string }>();
  const setBatchSession = useSetAtom(batchSessionAtom);
  const { trackEvent } = useTrackEvent();

  const { patients } = session;
  const total = patients.length;
  // Derive index from URL so browser back/forward always reflects the correct position.
  // Math.max guards the -1 case (URL doesn't match any patient — shouldn't happen in normal flow).
  const currentIndex = Math.max(
    0,
    patients.findIndex((p) => String(p.unifiedId) === unifiedId),
  );
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === total - 1;
  const displayNum = currentIndex + 1;

  const handlePrevious = () => {
    const prevIndex = currentIndex - 1;
    history.push(
      buildClaimPath(
        patients[prevIndex].unifiedId,
        patients[prevIndex].estimatedVisitId,
        patients[prevIndex].imagingPatientId,
      ),
    );
  };

  const handleNext = () => {
    if (isLast) {
      const attempted = Object.keys(session.results).length;
      trackEvent(mixpanelEvents.batchReviewFinished, {
        totalCount: total,
        attemptedCount: attempted,
        skippedCount: total - attempted,
      });
      history.push(routesConstants.CLAIMS_BATCH_SUMMARY);
    } else {
      const nextIndex = currentIndex + 1;
      history.push(
        buildClaimPath(
          patients[nextIndex].unifiedId,
          patients[nextIndex].estimatedVisitId,
          patients[nextIndex].imagingPatientId,
        ),
      );
    }
  };

  const handleExit = () => {
    setBatchSession(null);
    history.push(routesConstants.CLAIMS);
  };

  return (
    <div
      className="vai-bg-navy-500 vai-h-16 vai-w-full vai-flex vai-items-center vai-px-6 vai-relative vai-font-inter"
      style={{ flexShrink: 0 }}
    >
      {/* Left: attachment label + optional injected content (e.g. push error) */}
      <div className="vai-flex vai-items-center vai-gap-4">
        <span className="vai-text-white vai-font-medium vai-text-sm">
          <Trans>
            Attachment {displayNum} of {total}
          </Trans>
        </span>
        {leftContent && <div className="vai-ml-8 vai-mr-8">{leftContent}</div>}
      </div>

      {/* Center: Previous | action buttons | Next */}
      <div className="vai-absolute vai-left-1/2 vai-top-1/2 -vai-translate-x-1/2 -vai-translate-y-1/2 vai-flex vai-items-center vai-gap-2">
        <Button
          variant="ghost"
          onClick={handlePrevious}
          disabled={isFirst}
          title={t`Previous patient`}
          className="vai-text-white hover:vai-text-white hover:vai-bg-white/10 disabled:vai-opacity-40"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="vai-mr-2" />
          <Trans>Previous</Trans>
        </Button>

        {rightContent}

        <Button
          variant="ghost"
          onClick={handleNext}
          title={isLast ? t`Finish batch` : t`Next patient`}
          className="vai-text-white hover:vai-text-white hover:vai-bg-white/10"
        >
          <Trans>Next</Trans>
          <FontAwesomeIcon icon={faArrowRight} className="vai-ml-2" />
        </Button>
      </div>

      {/* Right: Exit */}
      <div className="vai-ml-auto">
        <Button
          variant="ghost"
          onClick={handleExit}
          title={t`Exit batch review`}
          className="vai-text-white hover:vai-text-white hover:vai-bg-white/10"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>
    </div>
  );
};
